import {
  buildPlansEndpoint,
  buildOperatorsEndpoint,
  PLAN_TYPES,
  type PlanType,
} from '../config/api';

/**
 * Fields of `plan.extra` returned by the API. Mobile fields are always
 * present; internet fields are only present on box offers.
 *
 * The index signature covers keys read dynamically: the advanced options
 * registry (MobileAdvancedOptionsRegistry on the PHP side) names its
 * criteria with an arbitrary `extraKey` — `multisim`, `5g-plus`,
 * `is_blocked_plan`… — whose list lives outside this repo.
 */
export interface PlanExtra {
  data: { data: number; data_unit: string };
  data_europe: { data: number; data_unit: string };
  esim: boolean;
  is_non_binding_plan: boolean;
  // The API returns either a number or the string '0' — compared against both in applyFilters.
  engagement_duration: number | string;
  internet_type?: (string | { value: string })[];
  internet_speed?: number;
  download_speed?: number;
  included_tv_player?: boolean;
  included_channels_nbr?: number;
  [extraKey: string]: unknown;
}

export interface Plan {
  id: string;
  name: string;
  type: string;
  operator: Operator;
  score: number;
  extra: PlanExtra;
  offer: {
    price: { price: number; crossed_price: string; promo_code?: string; note?: string };
    price_note?: string;
    url_lemon: string;
    offer_duration: string;
    raw_offer_duration?: string;
  };
  box?: { wifi?: string };
  visible_infos: string[];
}

export interface Operator {
  id: string;
  name: string;
  slug: string;
  logo: string;
  networks?: { name: string; slug: string }[];
}

class FrandroidAPI {
  private async fetchAPI<T>(endpoint: string): Promise<T> {
    const url = `/wp-json/humanoid/v1${endpoint}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // === Comparateurs ===

  async getPlans(planType: PlanType = PLAN_TYPES.MOBILE): Promise<Plan[]> {
    const response = await this.fetchAPI<{ data: Plan[] }>(buildPlansEndpoint(planType));
    return response.data;
  }

  async getOperators(planType: PlanType = PLAN_TYPES.MOBILE): Promise<Operator[]> {
    const response = await this.fetchAPI<{ data: Operator[] }>(buildOperatorsEndpoint(planType));
    return response.data ?? [];
  }
}

export const frandroidAPI = new FrandroidAPI();
